// SMSF State Migration Wrapper
// Provides backwards compatibility while migrating from useState to Context
// Allows gradual migration of components

import React from 'react';
import { useSMSFState, useSMSFDispatch, SMSF_ACTIONS } from './SMSFStateContext';

/**
 * Hook that provides backwards-compatible state and setters
 * Maps old individual state variables to new context structure
 */
export const useLegacySMSFState = () => {
    const state = useSMSFState();
    const dispatch = useSMSFDispatch();
    
    // Create setter functions that match the old useState pattern
    const setSmsfData = (data) => {
        dispatch({ type: SMSF_ACTIONS.SET_SMSF_DATA, payload: typeof data === 'function' ? data(state.smsfData) : data });
    };
    
    const setMemberProfile = (profile) => {
        dispatch({ type: SMSF_ACTIONS.SET_MEMBER_PROFILE, payload: typeof profile === 'function' ? profile(state.memberProfile) : profile });
    };
    
    const setConstraints = (constraints) => {
        dispatch({ type: SMSF_ACTIONS.SET_CONSTRAINTS, payload: typeof constraints === 'function' ? constraints(state.constraints) : constraints });
    };
    
    const setSmsfTrusteeCount = (count) => {
        dispatch({ type: 'SET_TRUSTEE_COUNT', payload: count });
    };
    
    const setSmsfEstablishedDate = (date) => {
        dispatch({ type: 'SET_ESTABLISHED_DATE', payload: date });
    };
    
    const setSmsfAuditFees = (fees) => {
        dispatch({ type: SMSF_ACTIONS.SET_FEE, payload: { type: 'audit', value: fees } });
    };
    
    const setSmsfAdminFees = (fees) => {
        dispatch({ type: SMSF_ACTIONS.SET_FEE, payload: { type: 'admin', value: fees } });
    };
    
    const setSmsfAccountingFees = (fees) => {
        dispatch({ type: SMSF_ACTIONS.SET_FEE, payload: { type: 'accounting', value: fees } });
    };
    
    const setSmsfConcessionalUsed = (amount) => {
        dispatch({ type: SMSF_ACTIONS.SET_CONTRIBUTION, payload: { type: 'concessionalUsed', value: amount } });
    };
    
    const setSmsfNonConcessionalUsed = (amount) => {
        dispatch({ type: SMSF_ACTIONS.SET_CONTRIBUTION, payload: { type: 'nonConcessionalUsed', value: amount } });
    };
    
    const setYourTbcUsed = (amount) => {
        dispatch({ type: SMSF_ACTIONS.SET_CONTRIBUTION, payload: { type: 'yourTbcUsed', value: amount } });
    };
    
    const setPartnerTbcUsed = (amount) => {
        dispatch({ type: SMSF_ACTIONS.SET_CONTRIBUTION, payload: { type: 'partnerTbcUsed', value: amount } });
    };
    
    const setSmsfDirectProperty = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'hasDirectProperty', value } });
    };
    
    const setSmsfPropertyValue = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'propertyValue', value } });
    };
    
    const setSmsfPropertyLoan = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'propertyLoan', value } });
    };
    
    const setSmsfPropertyInterestRate = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'propertyInterestRate', value } });
    };
    
    const setSmsfPropertyLoanTerm = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'propertyLoanTerm', value } });
    };
    
    const setSmsfPropertyRentalYield = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'propertyRentalYield', value } });
    };
    
    const setSmsfInHouseAssets = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'inHouseAssets', value } });
    };
    
    const setSmsfRelatedPartyLoans = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'relatedPartyLoans', value } });
    };
    
    const setSmsfCommercialProperty = (value) => {
        dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field: 'hasCommercialProperty', value } });
    };
    
    const setBeneficiaries = (beneficiaries) => {
        dispatch({ type: SMSF_ACTIONS.SET_BENEFICIARIES, payload: beneficiaries });
    };
    
    const setDeathBenefitNominations = (nominations) => {
        dispatch({ type: SMSF_ACTIONS.SET_DEATH_BENEFIT_NOMINATIONS, payload: nominations });
    };
    
    const setReversionaryPensionEnabled = (enabled) => {
        if ((enabled && !state.succession.reversionaryPensionEnabled) || 
            (!enabled && state.succession.reversionaryPensionEnabled)) {
            dispatch({ type: SMSF_ACTIONS.TOGGLE_REVERSIONARY_PENSION });
        }
    };
    
    const setReversionaryPensionAmount = (amount) => {
        dispatch({ type: SMSF_ACTIONS.SET_REVERSIONARY_AMOUNT, payload: amount });
    };
    
    const setSpouseAge = (age) => {
        dispatch({ type: SMSF_ACTIONS.SET_SPOUSE_AGE, payload: age });
    };
    
    const setTaxFreeComponent = (amount) => {
        dispatch({ type: SMSF_ACTIONS.SET_TAX_COMPONENT, payload: { component: 'taxFreeComponent', value: amount } });
    };
    
    const setTaxableComponent = (amount) => {
        dispatch({ type: SMSF_ACTIONS.SET_TAX_COMPONENT, payload: { component: 'taxableComponent', value: amount } });
    };
    
    const setAssetAllocationTargets = (targets) => {
        Object.entries(targets).forEach(([asset, value]) => {
            dispatch({ type: SMSF_ACTIONS.SET_ASSET_ALLOCATION_TARGET, payload: { asset, value } });
        });
    };
    
    const setAssetAllocationActual = (actual) => {
        Object.entries(actual).forEach(([asset, value]) => {
            dispatch({ type: SMSF_ACTIONS.SET_ASSET_ALLOCATION_ACTUAL, payload: { asset, value } });
        });
    };
    
    const setAssetValues = (values) => {
        Object.entries(values).forEach(([asset, value]) => {
            dispatch({ type: SMSF_ACTIONS.SET_ASSET_VALUE, payload: { asset, value } });
        });
    };
    
    const setAssetPerformance = (performance) => {
        Object.entries(performance).forEach(([asset, value]) => {
            dispatch({ type: SMSF_ACTIONS.SET_ASSET_PERFORMANCE, payload: { asset, value } });
        });
    };
    
    const setActiveTab = (tab) => {
        dispatch({ type: SMSF_ACTIONS.SET_ACTIVE_TAB, payload: tab });
    };
    
    const setShowTutorial = (show) => {
        if ((show && !state.showTutorial) || (!show && state.showTutorial)) {
            dispatch({ type: SMSF_ACTIONS.TOGGLE_TUTORIAL });
        }
    };
    
    // Return state and setters in the old format
    return {
        // State
        activeTab: state.activeTab,
        showTutorial: state.showTutorial,
        smsfData: state.smsfData,
        memberProfile: state.memberProfile,
        constraints: state.constraints,
        smsfTrusteeCount: state.smsfTrusteeCount,
        smsfEstablishedDate: state.smsfEstablishedDate,
        smsfAuditFees: state.fees.audit,
        smsfAdminFees: state.fees.admin,
        smsfAccountingFees: state.fees.accounting,
        smsfConcessionalUsed: state.contributions.concessionalUsed,
        smsfNonConcessionalUsed: state.contributions.nonConcessionalUsed,
        yourTbcUsed: state.contributions.yourTbcUsed,
        partnerTbcUsed: state.contributions.partnerTbcUsed,
        smsfDirectProperty: state.property.hasDirectProperty,
        smsfPropertyValue: state.property.propertyValue,
        smsfPropertyLoan: state.property.propertyLoan,
        smsfPropertyInterestRate: state.property.propertyInterestRate,
        smsfPropertyLoanTerm: state.property.propertyLoanTerm,
        smsfPropertyRentalYield: state.property.propertyRentalYield,
        smsfInHouseAssets: state.property.inHouseAssets,
        smsfRelatedPartyLoans: state.property.relatedPartyLoans,
        smsfCommercialProperty: state.property.hasCommercialProperty,
        deathBenefitNominations: state.succession.deathBenefitNominations,
        reversionaryPensionEnabled: state.succession.reversionaryPensionEnabled,
        reversionaryPensionAmount: state.succession.reversionaryPensionAmount,
        spouseAge: state.succession.spouseAge,
        beneficiaries: state.succession.beneficiaries,
        taxFreeComponent: state.succession.taxFreeComponent,
        taxableComponent: state.succession.taxableComponent,
        assetAllocationTargets: state.assetAllocation.targets,
        assetAllocationActual: state.assetAllocation.actual,
        assetValues: state.assetAllocation.values,
        assetPerformance: state.assetAllocation.performance,
        
        // Setters
        setActiveTab,
        setShowTutorial,
        setSmsfData,
        setMemberProfile,
        setConstraints,
        setSmsfTrusteeCount,
        setSmsfEstablishedDate,
        setSmsfAuditFees,
        setSmsfAdminFees,
        setSmsfAccountingFees,
        setSmsfConcessionalUsed,
        setSmsfNonConcessionalUsed,
        setYourTbcUsed,
        setPartnerTbcUsed,
        setSmsfDirectProperty,
        setSmsfPropertyValue,
        setSmsfPropertyLoan,
        setSmsfPropertyInterestRate,
        setSmsfPropertyLoanTerm,
        setSmsfPropertyRentalYield,
        setSmsfInHouseAssets,
        setSmsfRelatedPartyLoans,
        setSmsfCommercialProperty,
        setBeneficiaries,
        setDeathBenefitNominations,
        setReversionaryPensionEnabled,
        setReversionaryPensionAmount,
        setSpouseAge,
        setTaxFreeComponent,
        setTaxableComponent,
        setAssetAllocationTargets,
        setAssetAllocationActual,
        setAssetValues,
        setAssetPerformance
    };
};

/**
 * Helper function to convert old state format to new format
 */
export const convertLegacyState = (legacyState) => {
    return {
        activeTab: legacyState.activeTab || 'tutorial',
        showTutorial: legacyState.showTutorial || false,
        smsfData: legacyState.smsfData || {},
        memberProfile: legacyState.memberProfile || {},
        constraints: legacyState.constraints || {},
        smsfTrusteeCount: legacyState.smsfTrusteeCount || 2,
        smsfEstablishedDate: legacyState.smsfEstablishedDate || '',
        fees: {
            audit: legacyState.smsfAuditFees || 1500,
            admin: legacyState.smsfAdminFees || 2000,
            accounting: legacyState.smsfAccountingFees || 3000
        },
        contributions: {
            concessionalUsed: legacyState.smsfConcessionalUsed || 0,
            nonConcessionalUsed: legacyState.smsfNonConcessionalUsed || 0,
            yourTbcUsed: legacyState.yourTbcUsed || 0,
            partnerTbcUsed: legacyState.partnerTbcUsed || 0
        },
        property: {
            hasDirectProperty: legacyState.smsfDirectProperty || false,
            propertyValue: legacyState.smsfPropertyValue || 0,
            propertyLoan: legacyState.smsfPropertyLoan || 0,
            propertyInterestRate: legacyState.smsfPropertyInterestRate || 5.5,
            propertyLoanTerm: legacyState.smsfPropertyLoanTerm || 25,
            propertyRentalYield: legacyState.smsfPropertyRentalYield || 4.0,
            inHouseAssets: legacyState.smsfInHouseAssets || 0,
            relatedPartyLoans: legacyState.smsfRelatedPartyLoans || 0,
            hasCommercialProperty: legacyState.smsfCommercialProperty || false
        },
        assetAllocation: {
            targets: legacyState.assetAllocationTargets || {},
            actual: legacyState.assetAllocationActual || {},
            values: legacyState.assetValues || {},
            performance: legacyState.assetPerformance || {}
        },
        succession: {
            deathBenefitNominations: legacyState.deathBenefitNominations || [],
            reversionaryPensionEnabled: legacyState.reversionaryPensionEnabled || false,
            reversionaryPensionAmount: legacyState.reversionaryPensionAmount || 0,
            spouseAge: legacyState.spouseAge || 65,
            beneficiaries: legacyState.beneficiaries || [],
            taxFreeComponent: legacyState.taxFreeComponent || 0,
            taxableComponent: legacyState.taxableComponent || 0
        }
    };
};

export default useLegacySMSFState;
